import {
  AfterContentChecked,
  AfterContentInit,
  Component,
  ContentChildren,
  ElementRef,
  HostListener,
  Input,
  QueryList,
  ViewChild
} from '@angular/core';
import {AccordionItemComponent} from './accordion-item/accordion-item.component';

@Component({
  selector: 'ds-accordion',
  templateUrl: './accordion.component.html',
  styleUrls: ['./accordion.component.scss'],
})
export class AccordionComponent implements AfterContentInit, AfterContentChecked {
  @Input() isLocalStorageBacked = false;
  @Input() localStorageKeyPrefix = '';
  @Input() localStorageKeySuffix = '';
  @Input() isExpandAllShown = false;
  @Input() isFirstItemOpened = false;
  @Input() isAllExpanded = false;
  @Input() expandAllText = 'Expand All';
  @Input() collapseAllText = 'Collapse All';
  @Input() borderColor = 'grey-4';
  @Input() small = false;
  @Input() indented = false;
  /** selects  AccordionItems by type (the default way to do it) */
  @ContentChildren(AccordionItemComponent) accordionItemComponents: QueryList<AccordionItemComponent>;
  /** selects  AccordionItems by #accordionItem. Please extend AccordionItemComponent for the component you use this with */
  @ContentChildren('accordionItem') accordionItems: QueryList<any>;
  @ViewChild('expandAllButton') expandAllButton: ElementRef;
  @ViewChild('collapseAllButton') collapseAllButton: ElementRef;
  activeIds: string[] = [];
  ariaControls = 'aria-controls';
  listItems;


  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    this.handleKeyInput(event);
  }

  ngAfterContentInit(): void {
    this.setListItems();
    if (this.isLocalStorageHandlingEnabled()) {
      this.localStorageAwareAfterContentInit();
    } else {
      this.localStorageUnawareAfterContentInit();
    }
  }

  ngAfterContentChecked(): void {
    if (this.listItems) {
      let activeIds = [];
      this.listItems.forEach(item => {
        if (item.accordion.activeIds[0]) {
          activeIds = [...activeIds, ...item.accordion.activeIds];
        }
      });
      this.activeIds = activeIds;
      if (this.isLocalStorageHandlingEnabled()) {
        localStorage.setItem(this.localStorageKeyPrefix + this.localStorageKeySuffix, JSON.stringify(this.activeIds));
      }
    }
  }

  /**
   * Sets AccordionItems by type or by angular # selector.
   */
  private setListItems() {
    this.listItems = this.accordionItemComponents;
    if (this.accordionItems?.length) {
      this.listItems = this.accordionItems;
    }
  }

  private isLocalStorageHandlingEnabled() {
    return this.isLocalStorageBacked && this.localStorageKeyPrefix && this.localStorageKeySuffix;
  }

  private localStorageAwareAfterContentInit() {
    const activeIdsJsonArray: string = localStorage.getItem(this.localStorageKeyPrefix + this.localStorageKeySuffix);

    /*
     the case where the commaSeparatedActiveIds is null corresponds to the first time the accordion creation
    is triggered as a result of going from another page, after that the accordion items opening and closing will be
    controlled by its localstorage state.
     */

    if (activeIdsJsonArray === null) {
      this.listItems.forEach(item => {
        this.setItemProperties(item);
      });
      if (this.isAllExpanded) {
        this.expandAllSynchronously();
      }
      return;
    }

    // at this level we are sure we have an existing active items state stored in local-storage
    if (activeIdsJsonArray) {
      this.activeIds = JSON.parse(activeIdsJsonArray);
    }

    this.listItems.forEach(item => {
      if (this.activeIds.includes(item.accordionId)) {
        item.accordion.activeIds = item.accordionId;
      }

      this.setItemProperties(item);
    });
  }

  expandAllSynchronously() {
    this.listItems.forEach(item => {
      item.accordion.activeIds = item.accordionId;
    });
  }

  private localStorageUnawareAfterContentInit() {
    this.listItems.forEach(item => {
      if (this.isFirstItemOpened) {
        this.expandFirstAccordion();
      }
      item.borderColor = this.borderColor;
      item.small = this.small;
      item.indented = this.indented;
    });
    setTimeout(() => {
      if (this.isAllExpanded) {
        this.expandAll();
      }
    }, 0);
  }

  handleKeyInput(event: KeyboardEvent): void {
    switch (event.code) {
      case 'ArrowUp':
        this.setFocusToPrevious(this.getElementId(event));
        break;
      case 'ArrowDown':
        this.setFocusToNext(this.getElementId(event));
        break;
      case 'Home':
        this.setFocusToFirstItem();
        break;
      case 'End':
        this.setFocusToLastItem();
        break;
      default:
        break;
    }
  }

  /**
   * Open all accordion's panels
   * Move focus to the Collapse All button
   */
  expandAll() {
    this.listItems.forEach(item => {
      item.accordion.expandAll();
    });
    setTimeout(() => {
      if (this.collapseAllButton && this.collapseAllButton.nativeElement) {
        this.collapseAllButton.nativeElement.focus();
      }
    }, 0);
  }

  /**
   * Collapse all accordion's panels
   * Move focus to the Expand All button
   */
  collapseAll() {
    this.listItems.forEach(item => {
      item.accordion.collapseAll();
    });
    setTimeout(() => {
      if (this.expandAllButton && this.expandAllButton.nativeElement) {
        this.expandAllButton.nativeElement.focus();
      }
    }, 0);
  }

  /**
   * Expand accordion's first panel
   */
  expandFirstAccordion() {
    this.listItems.first.accordion.activeIds = this.listItems.first.accordionId;
  }

  /**
   * Set focus on the previous panel if the key up is pressed
   *
   * @param attr the focused panel id
   */
  setFocusToPrevious(attr: string): void {
    return this.listItems.toArray()[this.findIndexOfFocusedItem(attr) - 1] ?
      this.listItems.toArray()[this.findIndexOfFocusedItem(attr) - 1].panelToggle.nativeElement.focus() : this.setFocusToLastItem();
  }

  /**
   * Set focus on the next panel if the key down is pressed
   *
   * @param attr the focused panel id
   */
  setFocusToNext(attr: string): void {
    return this.listItems.toArray()[this.findIndexOfFocusedItem(attr) + 1] ?
      this.listItems.toArray()[this.findIndexOfFocusedItem(attr) + 1].panelToggle.nativeElement.focus() : this.setFocusToFirstItem();
  }

  /**
   * Set focus to the first panel
   */
  setFocusToFirstItem() {
    this.listItems.first.panelToggle.nativeElement.focus();
  }

  /**
   * Set focus to the last panel
   */
  setFocusToLastItem() {
    this.listItems.last.panelToggle.nativeElement.focus();
  }

  /**
   * Get the focused panel id
   *
   * @param event keyboard event
   */
  getElementId(event: KeyboardEvent): string {
    return (event.target as Element).getAttribute(this.ariaControls);
  }

  /**
   * Find the index of the focused panel
   *
   * @param attr the focused panel id
   * Return the index
   */
  findIndexOfFocusedItem(attr: string): number {
    return this.listItems.toArray().findIndex((item) => item.panelToggle.nativeElement.getAttribute(this.ariaControls) === attr);
  }

  private setItemProperties(item) {
    item.borderColor = this.borderColor;
    item.small = this.small;
    item.indented = this.indented;
  }
}
